import { z } from 'zod';

const objectIdString = z.string().min(1);

export const SurveyDispatchPeriodTypeSchema = z.enum(['Once', 'Periodic']);
export const SurveyDispatchStatusSchema = z.enum(['active', 'paused', 'completed', 'cancelled']);
export const SurveyDispatchSourceSchema = z.enum(['manual', 'scheduled', 'periodic']);
export const SurveyDispatchScheduleModeSchema = z.enum(['immediate', 'scheduled']);
export const SurveyDispatchIntervalUnitSchema = z.enum([
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year',
]);

const SurveyDispatchBaseShape = {
    id: z.string().optional(),
    formTemplate: objectIdString,
    name: z.string().trim().min(1).max(200),
    status: SurveyDispatchStatusSchema.optional(),
    assigneeUserIds: z.array(objectIdString).optional().default([]),
    assignmentTemplateId: objectIdString.optional().nullable(),
    scheduleMode: SurveyDispatchScheduleModeSchema.default('immediate'),
    startDate: z.string().trim().max(10).optional().nullable(),
    endDate: z.string().trim().max(10).optional().nullable(),
    periodType: SurveyDispatchPeriodTypeSchema.default('Once'),
    intervalValue: z.number().int().min(1).max(9999).optional().nullable(),
    intervalUnit: SurveyDispatchIntervalUnitSchema.optional().nullable(),
    nextRunAt: z.union([z.string(), z.date()]).optional().nullable(),
    lastRunAt: z.union([z.string(), z.date()]).optional().nullable(),
    runCount: z.number().int().min(0).optional(),
    lastError: z.string().optional().nullable(),
    createdBy: objectIdString.optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
};

function withScheduleRefine(schema) {
    return schema.superRefine((data, ctx) => {
        const mode = data.scheduleMode || 'immediate';
        if (mode === 'scheduled' && data.periodType === 'Periodic') {
            if (data.intervalValue == null || Number.isNaN(Number(data.intervalValue))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'intervalValue is required when periodType is Periodic',
                    path: ['intervalValue'],
                });
            }
            if (!data.intervalUnit) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'intervalUnit is required when periodType is Periodic',
                    path: ['intervalUnit'],
                });
            }
        }
        if (mode === 'scheduled' && !data.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'startDate is required when scheduleMode is scheduled',
                path: ['startDate'],
            });
        }
    });
}

export const SurveyDispatchSchema = withScheduleRefine(z.object(SurveyDispatchBaseShape));

export const CreateSurveyDispatchSchema = withScheduleRefine(
    z.object(SurveyDispatchBaseShape)
        .omit({
            id: true,
            nextRunAt: true,
            lastRunAt: true,
            runCount: true,
            lastError: true,
            createdBy: true,
            createdAt: true,
            updatedAt: true,
        })
        .extend({
            status: SurveyDispatchStatusSchema.optional().default('active'),
        }),
);

export const UpdateSurveyDispatchSchema = withScheduleRefine(
    z.object({
        name: z.string().trim().min(1).max(200).optional(),
        status: SurveyDispatchStatusSchema.optional(),
        assigneeUserIds: z.array(objectIdString).optional(),
        assignmentTemplateId: objectIdString.optional().nullable(),
        scheduleMode: SurveyDispatchScheduleModeSchema.optional(),
        startDate: z.string().trim().max(10).optional().nullable(),
        endDate: z.string().trim().max(10).optional().nullable(),
        periodType: SurveyDispatchPeriodTypeSchema.optional(),
        intervalValue: z.number().int().min(1).max(9999).optional().nullable(),
        intervalUnit: SurveyDispatchIntervalUnitSchema.optional().nullable(),
    }),
);
